import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { BadgeSelectCellValue } from "../../../../../components/custom-table/tableCell/cellTypes/BadgeSelectCell";
import { EditableTagListCellValue } from "../../../../../components/custom-table/tableCell/cellTypes/EditableTagListCell";
import { BadgeWithIconProps } from "../../../../../data-model/badgeWithIconProps";
import getPoc from "../../../../callingWorklist/table/utils/getPoc";
import type { PocConfigTableRowState } from "../data-model/pocAssignmentModel";
import {
  availabilityToBadgeId,
  AVAILABILITY_BADGE_LIST,
  MAX_VISIBLE_BATCH_TAGS,
} from "./pocAssignmentConstants";

/**
 * Builds a selectable badge cell value, guaranteeing the current value is
 * shown even when it is not part of the predefined option list.
 *
 * @param badgeList - Option list for the dropdown.
 * @param selectedId - Currently selected badge id.
 * @returns Badge cell value consumed by BadgeSelectCell.
 */
const buildBadgeSelectCellValue = (
  badgeList: BadgeWithIconProps[],
  selectedId: string,
): BadgeSelectCellValue => {
  const present = badgeList.some((badge) => badge.id === selectedId);
  const fullList =
    present || !selectedId
      ? badgeList
      : [
          ...badgeList,
          {
            id: selectedId,
            text: selectedId,
            bgColor: "#f5f5f5",
            textColor: "#333333",
          },
        ];
  return {
    selectedBadge:
      fullList.find((badge) => badge.id === selectedId) ?? fullList[0] ?? null,
    badgeList: fullList,
  };
};

export const POC_FIELD_KEYS = {
  poc: "poc",
  group: "group",
  batches: "batches",
  availability: "availability",
  cohort: "cohort",
  /** Column key for the delete control (class `delete-icon__pocConfigRowDelete`). */
  delete: "pocConfigRowDelete",
} as const;

/**
 * Table headers for the POC configuration grid.
 *
 * @returns Column definitions consumed by CustomTable.
 */
export const buildPocAssignmentTableHeaders = (): TableHeader[] => [
  {
    key: POC_FIELD_KEYS.poc,
    label: "POC",
    order: 0,
    width: 15,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    key: POC_FIELD_KEYS.group,
    label: "Group",
    order: 1,
    width: 17,
    sortable: true,
    type: TableCellType.SELECTABLE_BADGE,
  },
  {
    key: POC_FIELD_KEYS.batches,
    label: "Batches",
    order: 2,
    width: 30,
    type: TableCellType.EDITABLE_TAG_LIST,
  },
  {
    key: POC_FIELD_KEYS.availability,
    label: "Availability",
    order: 3,
    width: 15,
    sortable: true,
    type: TableCellType.SELECTABLE_BADGE,
  },
  {
    key: POC_FIELD_KEYS.cohort,
    label: "Cohort",
    order: 4,
    width: 14,
    sortable: true,
    type: TableCellType.SELECTABLE_BADGE,
  },
  {
    key: POC_FIELD_KEYS.delete,
    label: "",
    order: 5,
    width: 9,
    sortable: false,
    type: TableCellType.DELETE_ICON,
  },
];

export interface PocAssignmentTableRow {
  id: string;
  isDirty: boolean;
  [POC_FIELD_KEYS.poc]: string;
  [POC_FIELD_KEYS.group]: BadgeSelectCellValue;
  [POC_FIELD_KEYS.batches]: EditableTagListCellValue;
  [POC_FIELD_KEYS.availability]: BadgeSelectCellValue;
  [POC_FIELD_KEYS.cohort]: BadgeSelectCellValue;
  [POC_FIELD_KEYS.delete]: string;
}

/**
 * Maps POC config rows into the shape expected by CustomTable cell renderers.
 *
 * @param rows - Source rows from the poc_config_table metadata doc.
 * @param isDirty - Predicate indicating whether a row has unsaved edits.
 * @param cohortBadgeList - Cohort dropdown options.
 * @param groupBadgeList - Group dropdown options.
 * @returns Table rows ready for CustomTable. The batches column is editable:
 *   users can add or remove batch tags per row; at most four tags show in-cell
 *   with additional values behind a "+N more" control.
 */
export const buildPocAssignmentTableRows = (
  rows: PocConfigTableRowState[],
  isDirty: (id: string) => boolean,
  cohortBadgeList: BadgeWithIconProps[],
  groupBadgeList: BadgeWithIconProps[],
): PocAssignmentTableRow[] =>
  rows.map(
    (row): PocAssignmentTableRow => ({
      id: row.id,
      isDirty: isDirty(row.id),
      [POC_FIELD_KEYS.poc]: getPoc(row.poc) || row.poc,
      [POC_FIELD_KEYS.group]: buildBadgeSelectCellValue(
        groupBadgeList,
        row.group,
      ),
      [POC_FIELD_KEYS.batches]: {
        values: row.batch ?? [],
        placeholder: "Add batch",
        emptyLabel: "No batches",
        maxVisibleTags: MAX_VISIBLE_BATCH_TAGS,
      },
      [POC_FIELD_KEYS.availability]: buildBadgeSelectCellValue(
        AVAILABILITY_BADGE_LIST,
        availabilityToBadgeId(row.availability),
      ),
      [POC_FIELD_KEYS.cohort]: buildBadgeSelectCellValue(
        cohortBadgeList,
        row.cohort,
      ),
      [POC_FIELD_KEYS.delete]: getPoc(row.poc) || row.poc,
    }),
  );
